import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Badge variants synced from designs/design-tokens.pen
 *
 * .pen primitives mapped:
 *   ui/badge-active            → variant="default" (accent tint + dot)
 *   ui/badge-error             → variant="error"
 *   ui/badge-pending           → variant="warning"
 *   ui/badge-selected          → variant="selected"
 *   ui/badge-tag               → variant="outline"
 *   ui/badge-status-connected  → variant="status-success"
 *   ui/badge-status-disconnected → variant="status-error"
 *
 * Badges use AppKit pill radius, compact spacing, and mini typography.
 * Status dots: 6x6 compact rounded squares.
 */
const badgeVariants = cva(
  "inline-flex items-center rounded-pill px-appkit-xs text-appkit-mini font-medium backdrop-blur-glass-lg",
  {
    variants: {
      variant: {
        /* ui/badge-active: accent tint bg, accent border, accent text */
        default:
          "h-[22px] gap-appkit-2xs border border-badge-active-stroke bg-badge-active-fill text-accent",
        /* ui/badge-error: danger tint bg, danger border, danger text */
        error:
          "h-[22px] gap-appkit-2xs border border-badge-error-stroke bg-badge-error-fill text-danger",
        /* ui/badge-pending: warning tint bg, warning border, warning text */
        warning:
          "h-[22px] gap-appkit-2xs border border-badge-pending-stroke bg-badge-pending-fill text-warning",
        /* ui/badge-selected: selected tint bg, selected border, selected text */
        selected:
          "h-[22px] gap-appkit-2xs border border-badge-selected-stroke bg-badge-selected-fill text-status-selected",
        /* ui/badge-tag: muted tint bg, muted border, muted text */
        outline:
          "h-[22px] border border-badge-tag-stroke bg-badge-tag-fill text-muted",
        /* ui/badge-status-connected: glass bg, glass border, accent text, pill */
        "status-success":
          "h-[22px] border border-btn-secondary-stroke bg-glass-fill-heavy text-accent",
        /* ui/badge-status-disconnected: glass bg, glass border, danger text, pill */
        "status-error":
          "h-[22px] border border-btn-secondary-stroke bg-glass-fill-heavy text-danger",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    /** Show a status dot before the text */
    dot?: boolean;
    "data-testid"?: string;
  };

export function Badge({
  className,
  variant,
  dot,
  "data-testid": testId,
  children,
  ...props
}: BadgeProps) {
  const showDot =
    dot ??
    (variant === "default" ||
      variant === "error" ||
      variant === "warning" ||
      variant === "selected");

  const dotColorMap: Record<string, string> = {
    default: "bg-accent",
    error: "bg-danger",
    warning: "bg-warning",
    selected: "bg-status-selected",
  };

  return (
    <span
      className={cn(badgeVariants({ variant, className }))}
      data-testid={testId}
      {...props}
    >
      {showDot && (
        <span
          className={cn(
            "h-1.5 w-1.5 shrink-0 rounded-[3px]",
            dotColorMap[variant ?? "default"],
          )}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}

export { badgeVariants };
export type { BadgeProps };
